import React, { useState, useEffect, useContext } from "react";
import DaumPostcode from "react-daum-postcode";
import "./BuildingRegistrationModal.css";
import { BuildingContext } from "../../contexts/BuildingContext";
import WarningLoginModal from "./WarningLoginModal";

const BuildingRegistrationModal = ({ isOpen, onClose, onRegister }) => {
  const [buildingName, setBuildingName] = useState("");
  const [roadAddress, setRoadAddress] = useState("");
  const [jibunAddress, setJibunAddress] = useState("");
  const [buildingData, setBuildingData] = useState(null);
  const [isDaumPostcodeOpen, setIsDaumPostcodeOpen] = useState(false);
  const [payload, setPayload] = useState({
    buildingInfo: null,
    floorInfoList: null,
  });
  const [bun, setBun] = useState("0000");
  const [ji, setJi] = useState("0000");
  const [isLoading, setIsLoading] = useState(false); // 로딩 상태 추가
  const {
    addBuilding,
    fetchBuildingDetails,
    fetchUserAuth,
    userRole,
    buildings,
    setIsWarningModalOpen,
  } = useContext(BuildingContext);

  const BACK_API_BASE_URL = process.env.REACT_APP_BACK_API_BASE_URL;
  const API_KEY = process.env.REACT_APP_OPEN_API_KEY;
  const API_URL = "https://apis.data.go.kr/1613000/BldRgstService_v2";

  const PATH_TITLE = "/getBrTitleInfo";
  const PATH_FLOOR = "/getBrFlrOulnInfo";

  useEffect(() => {
    if (isOpen) {
      setBuildingName("");
      setRoadAddress("");
      setJibunAddress("");
      setIsDaumPostcodeOpen(true);
      setBuildingData(null);
      setIsLoading(false); // 모달 열 때 로딩 상태 초기화
      const script = document.createElement("script");
      script.src =
        "//t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js";
      script.async = true;
      document.body.appendChild(script);
      return () => {
        document.body.removeChild(script);
      };
    }
  }, [isOpen]);

  const handleComplete = async (data) => {
    const selectedAddress =
      data.autoJibunAddress || data.roadAddress || data.address;
    setRoadAddress(data.roadAddress || data.address);
    setJibunAddress(data.autoJibunAddress || "");
    setIsDaumPostcodeOpen(false);
    setBuildingName(data.buildingName);
    try {
      const sigunguCd = data.sigunguCode; // 시군구 코드
      const bjdongCd = data.bcode.substring(5); // 법정동 코드
      const platGbCd = "0"; // 대지구분 코드 기본값
      let bun = "0000";
      let ji = "0000";
      let bldName = "";
      let rdAddress = "";
      const jibun =
        data.jibunAddress !== "" ? data.jibunAddress : data.autoJibunAddress;
      if (jibun) {
        const addressParts = jibun.split(" ");
        const bunJiParts = addressParts[addressParts.length - 1].split("-");
        bun = bunJiParts[0].padStart(4, "0");
        if (bunJiParts.length > 1) {
          ji = bunJiParts[1].padStart(4, "0");
        }
        bldName = data.buildingName;
        rdAddress = data.address;
        setBun(bun);
        setJi(ji);
      }
      var buildingInfo = {
        buildingName: bldName,
        address: rdAddress,
      };
      var floorInfoList = [];

      setIsLoading(true); // 데이터 로딩 시작

      const response = await fetch(
        `${API_URL}${PATH_TITLE}?serviceKey=${encodeURIComponent(
          API_KEY
        )}&sigunguCd=${sigunguCd}&bjdongCd=${bjdongCd}&bun=${bun}&ji=${ji}&platGbCd=${platGbCd}&_type=json`
      );

      const textResponse = await response.text();

      if (response.ok) {
        const data = JSON.parse(textResponse);
        const item = data.response.body.items.item || [];
        if (item) {
          buildingInfo["buildingGroundFloor"] = item.grndFlrCnt;
          buildingInfo["buildingBasementFloor"] = item.ugrndFlrCnt;
          buildingInfo["buildingTotalArea"] = item.totArea;
          setBuildingData(buildingInfo);
        }
      } else {
        console.error("API 호출 실패", response.statusText);
      }

      const response2 = await fetch(
        `${API_URL}${PATH_FLOOR}?serviceKey=${encodeURIComponent(
          API_KEY
        )}&sigunguCd=${sigunguCd}&bjdongCd=${bjdongCd}&bun=${bun}&ji=${ji}&numOfRows=100&pageNo=1&platGbCd=${platGbCd}&_type=json`
      );
      const textResponse2 = await response2.text();

      if (response2.ok) {
        const data = JSON.parse(textResponse2);
        const item2 = data.response.body.items.item || [];
        var map = new Map();

        if (item2) {
          item2.forEach((e) => {
            if (e.flrGbCd === 10 || e.flrGbCd === 20) {
              const flrNo =
                e.flrGbCdNm === "지하" ? -parseInt(e.flrNo) : parseInt(e.flrNo);
              if (map.has(flrNo)) {
                var area = map.get(flrNo);
                map.set(
                  flrNo,
                  parseFloat(parseFloat(area) + parseFloat(e.area)).toFixed(2)
                );
              } else {
                map.set(flrNo, parseFloat(e.area));
              }
            }
          });
          map.forEach((value, key) => {
            floorInfoList.push({
              floorNum: key,
              area: value,
            });
          });
        } else {
          alert("해당 주소의 건축물 정보를 찾을 수 없습니다.");
        }
      } else {
        console.error("API 호출 실패", response.statusText);
      }

      const payload = {
        buildingInfo: buildingInfo,
        floorInfoList: floorInfoList,
      };

      console.log("페이로드 : ", payload);
      setPayload(payload);
      setIsLoading(false); // 데이터 로딩 완료
    } catch (error) {
      alert("건축물 정보를 불러오는 중 오류가 발생했습니다.");
      console.error("Fetch error: ", error);
      setIsLoading(false); // 에러 발생 시에도 로딩 상태 초기화
    }
  };

  const handleRegister = async () => {
    if (!buildingName || !roadAddress) {
      alert("먼저 건물명과 주소를 입력해주세요.");
      return;
    }
    if (!payload.buildingInfo || !payload.floorInfoList) {
      alert("건물 정보를 완전히 입력하지 않았습니다.");
      return;
    }

    const token = sessionStorage.getItem("token");
    const response3 = await fetch(
      `${BACK_API_BASE_URL}/api/building/register`,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        method: "POST",
        body: JSON.stringify(payload),
      }
    );

    if (response3.ok) {
      window.location.reload();
      onClose();
    } else {
      alert("건물 등록에 실패했습니다.");
    }
  };

  const handleChangeAddress = () => {
    setBuildingName("");
    setRoadAddress("");
    setJibunAddress("");
    setIsDaumPostcodeOpen(true);
    setPayload({ buildingInfo: null, floorInfoList: null });
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="building-registration-modal-header">
          <h2>건물 추가</h2>
          <button
            className="building-registration-modal-close-button"
            onClick={onClose}
          >
            <img
              src="/modalCloseIcon.png"
              alt="Close"
              className="building-registration-modal-close-icon"
            />
          </button>
        </div>
        {isDaumPostcodeOpen ? (
          <div className="postcode-container">
            <DaumPostcode onComplete={handleComplete} />
          </div>
        ) : (
          <>
            <div className="address-input-container">
              <label>건물 주소</label>
              <div className="modal-address-area">
                <input type="text" value={roadAddress} readOnly />
                <button
                  className="change-address-button"
                  onClick={handleChangeAddress}
                >
                  <img
                    src="/changeAddressIcon.png"
                    alt="Change Address"
                    className="change-address-icon"
                  />
                  주소변경
                </button>
              </div>
            </div>
            <label>건물명</label>
            <input
              type="text"
              value={buildingName}
              onChange={(e) => {
                var tmp = { ...payload };
                tmp.buildingInfo.buildingName = e.target.value;
                setBuildingName(e.target.value);
                setPayload(tmp);
              }}
              placeholder="건물명을 입력하세요"
              className="modal-name-area"
            />
            <div className="modal-done-button">
              <button className="modal-close-button" onClick={onClose}>
                취소
              </button>
              <button
                className="modal-complete-button"
                onClick={handleRegister}
                disabled={isLoading}
              >
                입력 완료
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default BuildingRegistrationModal;
